import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiFolder, FiArrowRight, FiGrid, FiList, FiCalendar, FiTable,
  FiSettings, FiShare2, FiUsers, FiCheckCircle, FiLayout
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const ProjectManagement = () => {
  const views = [
    {
      icon: FiGrid,
      title: 'Kanban Board',
      description: 'Visualize work as cards moving through columns. Perfect for workflow management.',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
    },
    {
      icon: FiList,
      title: 'List View',
      description: 'Traditional list format with all task details visible. Great for detailed planning.',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600'
    },
    {
      icon: FiCalendar,
      title: 'Calendar View',
      description: 'See tasks organized by due dates on a calendar. Ideal for deadline management.',
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600'
    },
    {
      icon: FiTable,
      title: 'Table View',
      description: 'Spreadsheet-like view for bulk editing and detailed data management.',
      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/personal/projects" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/personal" className="hover:text-blue-600">Personal Workspace</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Project Management</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
              <FiFolder className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Project Management</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Organize your work into projects with multiple views, settings, and collaboration features.
            </p>
          </div>

          {/* Creating Projects */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Creating a Project</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Projects help you organize related tasks together. Here's how to create one:
            </p>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Click "+ New Project"</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Find this button in the sidebar or dashboard</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Enter project details</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Add a name, description, and choose a color</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Select default view</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Choose Kanban, List, Calendar, or Table view</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Configure settings</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Set visibility, labels, and custom columns</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Project Views */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Project Views</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Switch between different views to visualize your work the way you prefer:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {views.map((view, index) => {
                const Icon = view.icon;
                return (
                  <div
                    key={index}
                    className="border border-gray-200 dark:border-gray-700 rounded-xl p-6"
                  >
                    <div className={`w-12 h-12 rounded-xl ${view.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{view.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{view.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Project Settings */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Project Settings</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Customize your project to fit your workflow:
            </p>

            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                <FiSettings className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">General Settings</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Update project name, description, color, and archive status.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                <FiLayout className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">Columns & Statuses</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Customize task statuses and Kanban columns. Add, remove, or reorder as needed.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                <FiUsers className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">Members</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Invite team members and assign roles (Admin, Editor, Viewer).
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
                <FiShare2 className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">Sharing & Visibility</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Control who can see the project - private, team, or public link.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Project Roles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Project Member Roles</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Assign different roles to project members to control their access:
            </p>

            <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-xl">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Role</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">View</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Edit Tasks</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Settings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Admin</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Editor</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Viewer</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/teams"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Personal Teams</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default ProjectManagement;
